// AXIS ZERO - Streak System
// High Mode at streak >= 5 with color switch and +3% speed
// Near-miss streak multiplier mechanic

import { CONFIG } from './Config';
import { audioManager } from './AudioManager';

export class StreakSystem {
  constructor() {
    this.streak = 0;
this.maxStreak = 0;
    this.lastNearMissTime = 0;
    this.streakTimeout = CONFIG.STREAK_TIMEOUT;
    this.displayStreak = 0;
    this.displayTimer = 0;
    this.bonusPoints = 0;
    this.glowMultiplier = 1;
    
    // Deduplication
    this.countedObstacleIds = new Set();
    this.cooldownMs = 400;
    this.lastIncrementTime = 0;
    this.isDead = false;
    
    // High Mode state
    this.isHighMode = false;
    this.wasHighMode = false;
    this.highModeSpeedMult = 1;
    this.targetSpeedMult = 1;
    
    // High Mode entry text
    this.showHighModeText = false;
    this.highModeTextTimer = 0;
      this.jumpBuffTimer = 0;
  }

  reset() {
    this.streak = 0;
this.maxStreak = 0;
    this.lastNearMissTime = 0;
    this.displayStreak = 0;
    this.displayTimer = 0;
    this.bonusPoints = 0;
    this.glowMultiplier = 1;
    this.countedObstacleIds.clear();
    this.lastIncrementTime = 0;
    this.isDead = false;
    this.isHighMode = false;
    this.wasHighMode = false;
    this.highModeSpeedMult = 1;
    this.targetSpeedMult = 1;
    this.showHighModeText = false;
    this.highModeTextTimer = 0;
      this.jumpBuffTimer = 0;
  }

  onDeath() {
    this.isDead = true;
    this.streak = 0;
this.glowMultiplier = 1;
    this.isHighMode = false;
    this.targetSpeedMult = 1;
  }

  registerNearMiss(gameTime, obstacleId = null) {
    const now = Date.now();
    
    if (this.isDead) return null;
    if (now - this.lastIncrementTime < this.cooldownMs) return null;
    if (obstacleId !== null && this.countedObstacleIds.has(obstacleId)) return null;
    
    if (obstacleId !== null) {
      this.countedObstacleIds.add(obstacleId);
    }
    
    this.lastIncrementTime = now;
    this.lastNearMissTime = gameTime;
    
    this.streak++;
    this.displayStreak = this.streak;
    this.displayTimer = 1.5;
    
    if (this.streak > this.maxStreak) {
      this.maxStreak = this.streak;
    }

    this.bonusPoints += 0.1 * this.streak;
    this.glowMultiplier = 1 + Math.min(this.streak * 0.12, 1.2);
    
    // Check for High Mode entry (streak >= 5)
    this.wasHighMode = this.isHighMode;
    this.isHighMode = this.streak >= CONFIG.HIGH_MODE_THRESHOLD;
    
    // Trigger entry effects
    const enteredHighMode = !this.wasHighMode && this.isHighMode;
    if (enteredHighMode) {
      this.showHighModeText = true;
      this.highModeTextTimer = 0.6;
      this.targetSpeedMult = CONFIG.HIGH_MODE_SPEED_MULT;
        this.jumpBuffTimer = 3.0;
      audioManager.playHighModeEntry();
    }

    return {
      streak: this.streak,
      bonus: 0.1 * this.streak,
      isNew: this.streak === this.maxStreak,
      enteredHighMode,
    };
  }

  update(deltaTime, gameTime) {
      if (this.jumpBuffTimer > 0) {
        this.jumpBuffTimer -= deltaTime;
      }
    // Streak timeout - drops streak gradually (prevents instant wipe + avoids NaN)
    // Safety guards (in case something ever corrupts state)
    if (!Number.isFinite(this.streak)) this.streak = 0;
    if (!Number.isFinite(this.bonusPoints)) this.bonusPoints = 0;

    if (this.streak > 0 && gameTime - this.lastNearMissTime > this.streakTimeout) {
      const wasInHighMode = this.isHighMode;

      // decay by 2, but DO IT ONCE per timeout window
      this.streak = Math.max(0, this.streak - 2);

      // restart timer so we don't subtract every frame
      this.lastNearMissTime = gameTime;

      // keep visuals/state consistent with the new streak
      this.displayStreak = this.streak;
      this.displayTimer = 1.0;
      this.glowMultiplier = 1 + Math.min(this.streak * 0.12, 1.2);

      // High Mode depends on streak
      this.isHighMode = this.streak >= CONFIG.HIGH_MODE_THRESHOLD;
      this.targetSpeedMult = this.isHighMode ? CONFIG.HIGH_MODE_SPEED_MULT : 1;

      // if we just exited high mode, clear the entry text flag
      if (wasInHighMode && !this.isHighMode) {
        this.showHighModeText = false;
        this.highModeTextTimer = 0;
      this.jumpBuffTimer = 0;
      }
    }

// Update display timer
    if (this.displayTimer > 0) {
      this.displayTimer -= deltaTime;
      if (this.displayTimer <= 0) {
        this.displayStreak = this.streak;
      }
    }
    
    // Update High Mode text timer
    if (this.highModeTextTimer > 0) {
      this.highModeTextTimer -= deltaTime;
      if (this.highModeTextTimer <= 0) {
        this.showHighModeText = false;
      }
    }
    
    // Smooth speed multiplier transition (0.2-0.3s)
    const transitionSpeed = 4; // ~0.25s
    if (this.highModeSpeedMult < this.targetSpeedMult) {
      this.highModeSpeedMult = Math.min(this.targetSpeedMult, this.highModeSpeedMult + deltaTime * transitionSpeed * 0.03);
    } else if (this.highModeSpeedMult > this.targetSpeedMult) {
      this.highModeSpeedMult = Math.max(this.targetSpeedMult, this.highModeSpeedMult - deltaTime * transitionSpeed * 0.03);
    }
    
    // Clean up old IDs
    if (this.countedObstacleIds.size > 50) {
      const idsArray = Array.from(this.countedObstacleIds);
      this.countedObstacleIds = new Set(idsArray.slice(-25));
    }
  }

  getDisplayInfo() {
    return {
      streak: this.displayStreak,
      shouldShow: this.displayTimer > 0 || this.streak > 0,
      alpha: this.displayTimer > 0 ? Math.min(1, this.displayTimer) : (this.streak > 0 ? 0.7 : 0),
      glowMultiplier: this.glowMultiplier,
      isHighMode: this.isHighMode,
      showHighModeText: this.showHighModeText,
      highModeTextAlpha: this.getHighModeTextAlpha(),
    };
  }

  getHighModeTextAlpha() {
    if (!this.showHighModeText) return 0;
    const total = 0.6;
    const fadeIn = 0.15;
    const fadeOut = 0.45;
    const elapsed = total - this.highModeTextTimer;
    
    if (elapsed < fadeIn) {
      return elapsed / fadeIn;
    } else if (elapsed < total - fadeOut) {
      return 1;
    } else {
      return this.highModeTextTimer / fadeOut;
    }
  }

  // Get current world colors based on High Mode
  getWorldColors() {
    if (this.isHighMode) {
      return {
        ...CONFIG.COLORS,
        ...CONFIG.COLORS_HIGH_MODE,
      };
    }
    return CONFIG.COLORS;
  }

  // Get speed multiplier for High Mode
  getSpeedMultiplier() {
    return this.highModeSpeedMult;
  }

  getBonusPoints() {
    return this.bonusPoints;
  }

  getGlowMultiplier() {
    const base = this.glowMultiplier;
    return this.isHighMode ? base * CONFIG.HIGH_MODE_GLOW_INCREASE : base;
  }
  
  isInHighMode() {
    return this.isHighMode;
  }
}

export default StreakSystem;
